/**
 * F37 (Wave 2D) — admin review-tag-catalog CMS page.
 *
 * Route: `/recommendation-catalog/tags`
 * Permission: `recommendation_catalog:read` (page) + `recommendation_catalog:write`
 * (Add / Edit / Delete — hidden when the actor lacks write).
 *
 * Players pick these curated tags when reviewing a trainer (D1). Each is
 * sport-scoped (NULL = all sports — D9). Delete soft-deactivates an in-use
 * entry (409 CATALOG_ENTRY_IN_USE).
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { ReviewTagEntry, UpsertReviewTagInput } from '@bravemove/types';
import { ApiError } from '../../../auth/types';
import { adminRecommendationApi } from '../../../api/recommendation';
import { translateApiError } from '../../../auth/errors';
import { useAuth } from '../../../auth/useAuth';
import { PageHeader } from '../../../components/PageHeader';
import { ReviewTagEditDialog } from '../../../components/recommendation/ReviewTagEditDialog';

export function AdminReviewTagsPage() {
  const { t } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const canWrite = auth.adminUser?.permissions.includes('recommendation_catalog:write') ?? false;

  const [rows, setRows] = useState<readonly ReviewTagEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ReviewTagEntry | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReviewTagEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminRecommendationApi.listTags());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const openAdd = () => {
    setEditTarget(null);
    setEditError(null);
    setEditOpen(true);
  };
  const openEdit = (tag: ReviewTagEntry) => {
    setEditTarget(tag);
    setEditError(null);
    setEditOpen(true);
  };

  const handleSave = async (input: UpsertReviewTagInput): Promise<void> => {
    setEditError(null);
    try {
      if (editTarget) {
        const { code: _omit, ...patch } = input;
        void _omit;
        await adminRecommendationApi.updateTag(editTarget.code, patch);
      } else {
        await adminRecommendationApi.upsertTag(input);
      }
      setEditOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.catalog.toast_saved', { defaultValue: 'Saved' }),
      });
      await refresh();
    } catch (err) {
      setEditError(translateApiError(err, t));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminRecommendationApi.deleteTag(deleteTarget.code);
      setDeleteTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.catalog.toast_deleted', { defaultValue: 'Deleted' }),
      });
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CATALOG_ENTRY_IN_USE') {
        setDeleteTarget(null);
        toast.push({
          tone: 'info',
          title: t('recommendation:catalog.in_use_notice', {
            defaultValue: 'This entry is in use and was deactivated instead of deleted.',
          }),
        });
        await refresh();
      } else {
        toast.push({ tone: 'danger', title: translateApiError(err, t) });
      }
    } finally {
      setDeleting(false);
    }
  };

  const columns: readonly TableColumn<ReviewTagEntry>[] = [
    {
      key: 'name',
      header: t('recommendation:catalog.name_label', { defaultValue: 'Name' }),
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-[12.5px] text-ink">{row.name.en ?? row.code}</span>
          <span className="font-mono text-[11px] text-ink-3">{row.code}</span>
        </div>
      ),
    },
    {
      key: 'scope',
      header: t('recommendation:catalog.sport_scope_label', { defaultValue: 'Sport scope' }),
      cell: (row) =>
        row.sportScope === null ? (
          <span className="text-[12px] text-ink-3">
            {t('recommendation:catalog.sport_scope_all', { defaultValue: 'All sports' })}
          </span>
        ) : (
          <span className="text-[12px] text-ink-2 capitalize">
            {row.sportScope.map((s) => s.replace(/-/g, ' ')).join(', ')}
          </span>
        ),
    },
    {
      key: 'sort',
      header: t('recommendation:catalog.sort_order_label', { defaultValue: 'Sort order' }),
      cell: (row) => (
        <span className="font-mono text-[12px] text-ink-2 tabular-nums">{row.sortOrder}</span>
      ),
    },
    {
      key: 'active',
      header: t('recommendation:catalog.active_label', { defaultValue: 'Active' }),
      cell: (row) => (
        <Badge tone={row.active ? 'success' : 'neutral'} size="sm">
          {row.active
            ? t('admin:recommendation.catalog.active_yes', { defaultValue: 'Active' })
            : t('admin:recommendation.catalog.active_no', { defaultValue: 'Inactive' })}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: t('admin:recommendation.catalog.actions', { defaultValue: 'Actions' }),
            align: 'end' as const,
            cell: (row: ReviewTagEntry) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(row)}
                  data-testid={`tag-edit-${row.code}`}
                >
                  {t('admin:recommendation.catalog.edit_cta', { defaultValue: 'Edit' })}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(row)}
                  data-testid={`tag-delete-${row.code}`}
                >
                  {t('admin:recommendation.catalog.delete_cta', { defaultValue: 'Delete' })}
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="review-tags-page">
      <PageHeader
        eyebrow={t('admin:recommendation.catalog.eyebrow', { defaultValue: 'Recommendations' })}
        title={t('recommendation:catalog.tags_title', { defaultValue: 'Review tag catalog' })}
        subtitle={t('admin:recommendation.catalog.tags_subtitle', {
          defaultValue: 'Curated tags players pick when reviewing a trainer.',
        })}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={openAdd} data-testid="tag-add">
              {t('admin:recommendation.catalog.add_tag', { defaultValue: 'Add tag' })}
            </Button>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="review-tags-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:recommendation.catalog.tags_empty', {
            defaultValue: 'No review tags yet — add one to get started.',
          })}
        />
      ) : (
        <Card>
          <Table
            data={rows as ReviewTagEntry[]}
            columns={columns as TableColumn<ReviewTagEntry>[]}
            rowKey={(row) => row.code}
          />
        </Card>
      )}

      <ReviewTagEditDialog
        open={editOpen}
        tag={editTarget}
        onSave={handleSave}
        onClose={() => setEditOpen(false)}
        errorMessage={editError}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        destructive
        loading={deleting}
        title={t('admin:recommendation.catalog.tag_delete_title', {
          defaultValue: 'Delete this review tag?',
        })}
        description={t('admin:recommendation.catalog.tag_delete_body', {
          defaultValue:
            'If the tag is in use on existing reviews it will be deactivated instead of deleted.',
        })}
        confirmLabel={t('admin:recommendation.catalog.delete_cta', { defaultValue: 'Delete' })}
      />
    </div>
  );
}

export default AdminReviewTagsPage;
